import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, addDoc, doc, writeBatch, onSnapshot, query, orderBy, deleteDoc, updateDoc } from 'firebase/firestore';

// Time Log Creation Form Component
export const TimeLogCreationForm = ({
  newTimeLog,
  setNewTimeLog,
  handleAddTimeLog
}) => {
  return (
    <div className="create-post">
      <h2>Log New Activity</h2>
      <input
        type="text"
        placeholder="Activity name (e.g., Yoga, Internship, Volunteer Work)"
        value={newTimeLog.activity}
        onChange={(e) => setNewTimeLog({ ...newTimeLog, activity: e.target.value })}
        className="post-title-input"
      />
      <div className="project-type-selector">
        <label>Domain:</label>
        <select
          value={newTimeLog.domain}
          onChange={(e) => setNewTimeLog({ ...newTimeLog, domain: e.target.value })}
          className="project-type-dropdown"
        >
          <option value="">Select Domain</option>
          <option value="physical-activity">Physical Activity</option>
          <option value="internship">Internship/Work Experience</option>
          <option value="volunteer">Volunteer Work</option>
          <option value="clubs-&-classes">Clubs and Classes</option>
          <option value="in-school-leadership-position">In School Leadership Position</option>
          <option value="other">Other</option>
        </select>
      </div>
      <input
        type="number"
        step="0.5"
        min="0"
        placeholder="Hours (e.g., 2.5)"
        value={newTimeLog.hours}
        onChange={(e) => setNewTimeLog({ ...newTimeLog, hours: e.target.value })}
        className="post-title-input"
      />
      <div className="form-row">
        <div className="form-group half-width">
          <label>Start Date:</label>
          <input
            type="date"
            value={newTimeLog.startDate}
            onChange={(e) => setNewTimeLog({ ...newTimeLog, startDate: e.target.value })}
            className="post-title-input"
          />
        </div>
        <div className="form-group half-width">
          <label>End Date:</label>
          <input
            type="date"
            value={newTimeLog.endDate}
            onChange={(e) => setNewTimeLog({ ...newTimeLog, endDate: e.target.value })}
            className="post-title-input"
          />
        </div>
      </div>
      <textarea
        placeholder="Additional details about this activity (optional)..."
        value={newTimeLog.description}
        onChange={(e) => setNewTimeLog({ ...newTimeLog, description: e.target.value })}
        className="post-content-input"
        rows="3"
      />
      <button className="log-activity-button" onClick={handleAddTimeLog}>Log Activity</button>
    </div>
  );
};

// Time Log Display Component
export const TimeLogDisplay = ({
  timeLogs,
  currentUser,
  expandedTimeLogs,
  toggleTimeLogExpansion,
  handleToggleConsolidatedFeatured,
  consolidateTimeLogs,
  groupTimeLogsByGrade,
  studentId,
  handleDeleteConsolidated,
  handleDeleteEntry,
  hideHeader,
  // Optional editing props for student's pending section
  editingTimeLogId,
  editTimeLogForm,
  setEditTimeLogForm,
  handleStartEditTimeLog,
  handleSaveTimeLogEdit,
  handleCancelEditTimeLog
}) => {
  const viewingUserId = studentId || currentUser.uid;
  const userTimeLogs = timeLogs.filter(log => log.userId === viewingUserId);
  const pendingLogs = userTimeLogs.filter(log => log.status !== 'approved');
  const approvedLogs = userTimeLogs.filter(log => log.status === 'approved');
  
  if (userTimeLogs.length === 0) {
    return <p className="no-posts">No time logs yet. Start logging your activities above!</p>;
  }

  // Group logs by grade
  const logsByGrade = groupTimeLogsByGrade(approvedLogs);

  return (
    <div className="time-logs-container time-logging-section">
      {!hideHeader && (
        <>
          <h2>Your Time Logs</h2>
          <p>By default, logs do not appear on your transcript. Use the Star button to add logs to your transcript.</p>
          <p>Logs with the same activity name and domain will be consolidated into a single entry.</p>
        </>
      )}
      
      <div>
        {/* Awaiting Approval Section (for student's own view only) */}
        {(!studentId || studentId === currentUser.uid) && pendingLogs.length > 0 && (
          <div className="time-logs-container awaiting-approval-section">
            <h3>Awaiting Parent Approval</h3>
            <div className="time-logs-list">
              {pendingLogs.map(entry => {
                const isEditing = editingTimeLogId === entry.id;
                return (
                  <div key={entry.id} className="individual-time-log-entry awaiting-approval">
                    <div className="individual-time-log-header">
                      <span className="individual-hours">{entry.hours} hours</span>
                      <span className="individual-dates">
                        {new Date(entry.startDate).toLocaleDateString()} - {new Date(entry.endDate).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="individual-time-log-meta">
                      <span className="time-log-domain">{entry.domain.replace(/-/g, ' ').replace(/&/g, '&').replace(/\b\w/g, l => l.toUpperCase())}</span>
                      <span className="awaiting-badge">Pending</span>
                    </div>
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          className="post-title-input"
                          value={editTimeLogForm?.activity || ''}
                          onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, activity: e.target.value })}
                          placeholder="Activity name"
                        />
                        <div className="form-row">
                          <div className="form-group half-width">
                            <label>Domain</label>
                            <select
                              value={editTimeLogForm?.domain || ''}
                              onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, domain: e.target.value })}
                              className="project-type-dropdown"
                            >
                              <option value="">Select Domain</option>
                              <option value="physical-activity">Physical Activity</option>
                              <option value="internship">Internship/Work Experience</option>
                              <option value="volunteer">Volunteer Work</option>
                              <option value="clubs-&-classes">Clubs and Classes</option>
                              <option value="in-school-leadership-position">In School Leadership Position</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div className="form-group half-width">
                            <label>Hours</label>
                            <input
                              type="number"
                              step="0.5"
                              min="0"
                              value={editTimeLogForm?.hours || ''}
                              onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, hours: e.target.value })}
                              className="post-title-input"
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group half-width">
                            <label>Start Date</label>
                            <input
                              type="date"
                              value={editTimeLogForm?.startDate || ''}
                              onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, startDate: e.target.value })}
                              className="post-title-input"
                            />
                          </div>
                          <div className="form-group half-width">
                            <label>End Date</label>
                            <input
                              type="date"
                              value={editTimeLogForm?.endDate || ''}
                              onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, endDate: e.target.value })}
                              className="post-title-input"
                            />
                          </div>
                        </div>
                        <textarea
                          value={editTimeLogForm?.description || ''}
                          onChange={(e) => setEditTimeLogForm && setEditTimeLogForm({ ...editTimeLogForm, description: e.target.value })}
                          className="proposal-edit-textarea"
                          rows="3"
                          placeholder="Additional details..."
                        />
                        <div className="proposal-actions">
                          <button className="proposal-save-btn" onClick={handleSaveTimeLogEdit}>Save</button>
                          <button className="proposal-cancel-btn" onClick={handleCancelEditTimeLog}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <h5>{entry.activity}</h5>
                        {entry.description && (
                          <p className="individual-description">{entry.description}</p>
                        )}
                        <div className="proposal-actions">
                          {handleStartEditTimeLog && <button className="proposal-edit-btn" onClick={() => handleStartEditTimeLog(entry)}>Edit</button>}
                          <button className="proposal-reject-btn" onClick={() => handleDeleteEntry(entry.id)}>Delete</button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {/* Overall Summary */}
        <div className="domain-summary">
          <h4>Total Hours by Domain (All Grades)</h4>
          {(() => {
            // Calculate totals for each domain across all grades
            const domainTotals = {};
            approvedLogs.forEach(log => {
              if (!domainTotals[log.domain]) {
                domainTotals[log.domain] = 0;
              }
              domainTotals[log.domain] += log.hours;
            });

            return Object.entries(domainTotals)
              .sort(([,a], [,b]) => b - a)
              .map(([domain, totalHours]) => (
                <div key={domain} className="grade-domain-total">
                  <span className="domain-name">{domain.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                  <span className="domain-hours">{totalHours} hours</span>
                </div>
              ));
          })()}
        </div>

        {/* Grade-based Organization */}
        {logsByGrade.map(({ grade, logs }) => {
          const gradeLabel = grade === 'Unknown' ? 'Unknown Grade' : grade === 'Portfolio Defense' ? 'Portfolio Defense' : `${grade}th Grade`;
          const consolidatedLogs = consolidateTimeLogs(logs);
          
          return (
            <div key={grade} className="time-log-grade-section">
              <h4 className="time-log-grade-header">
                {gradeLabel}
                <span className="grade-total-hours">
                  ({logs.reduce((sum, log) => sum + log.hours, 0)} total hours)
                </span>
              </h4>

              {/* Grade Domain Summary */}
              <div className="grade-domain-summary">
                {(() => {
                  // Calculate totals for each domain within this grade
                  const gradeDomainTotals = {};
                  logs.forEach(log => {
                    if (!gradeDomainTotals[log.domain]) {
                      gradeDomainTotals[log.domain] = 0;
                    }
                    gradeDomainTotals[log.domain] += log.hours;
                  });

                  return Object.entries(gradeDomainTotals)
                    .sort(([,a], [,b]) => b - a)
                    .map(([domain, totalHours]) => (
                      <div key={domain} className="grade-domain-total">
                        <span className="domain-name">{domain.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        <span className="domain-hours">{totalHours} hours</span>
                      </div>
                    ));
                })()}
              </div>

              {/* Consolidated Logs for this Grade */}
              <div className="time-logs-list">
                {consolidatedLogs.map(consolidated => {
                  const activityKey = `${consolidated.activity.toLowerCase()}-${consolidated.domain}`;
                  const isExpanded = expandedTimeLogs.has(activityKey);
                  
                  return (
                    <div key={activityKey} className={`consolidated-time-log-entry ${consolidated.featured ? 'featured' : ''}`}>
                      <div 
                        className="consolidated-time-log-header"
                        onClick={() => toggleTimeLogExpansion(activityKey)}
                      >
                        <div className="consolidated-time-log-main">
                          <h5>{consolidated.activity}</h5>
                          <span className="time-log-domain">{consolidated.domain.replace(/-/g, ' ').replace(/&/g, '&').replace(/\b\w/g, l => l.toUpperCase())}</span>
                        </div>
                        <div className="consolidated-time-log-summary">
                          <span className="consolidated-hours">{consolidated.totalHours} hours</span>
                          <span className="consolidated-count">({consolidated.entryCount} entries)</span>
                          {currentUser && consolidated.entries[0]?.userId === currentUser.uid && (
                            <>
                               <button
                                className="star-button trash-button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteConsolidated(consolidated);
                                }}
                                title="Delete all entries in this activity"
                              >
                                <i className="fa-solid fa-trash"></i>
                              </button>
                              <button
                                className={`star-button ${consolidated.featured ? 'starred' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleConsolidatedFeatured(consolidated);
                                }}
                                title={consolidated.featured ? 'Remove from featured' : 'Add to featured'}
                              >
                                <i className={`fa-solid ${consolidated.featured ? 'fa-star' : 'fa-star'}`}></i>
                              </button>

                            </>
                          )}
                          <div className={`consolidated-arrow ${isExpanded ? 'expanded' : ''}`}>
                            ▶
                          </div>
                        </div>
                      </div>
                      <div className="consolidated-time-log-dates">
                        {new Date(consolidated.firstDate).toLocaleDateString()} - {new Date(consolidated.lastDate).toLocaleDateString()}
                      </div>
                      {isExpanded && (
                        <div className="consolidated-time-log-details">
                          {consolidated.entries.map(entry => (
                            <div key={entry.id} className={`individual-time-log-entry ${entry.featured ? 'featured' : ''}`}>
                              <div className="individual-time-log-header">
                                <span className="individual-hours">{entry.hours} hours</span>
                                <span className="individual-dates">
                                  {new Date(entry.startDate).toLocaleDateString()} - {new Date(entry.endDate).toLocaleDateString()}
                                </span>
                                {currentUser && entry.userId === currentUser.uid && (
                                  <button
                                    className="star-button trash-button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteEntry(entry.id);
                                    }}
                                    title="Delete this entry"
                                  >
                                    <i className="fa-solid fa-trash"></i>
                                  </button>
                                )}
                              </div>
                              {entry.description && (
                                <p className="individual-description">{entry.description}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Time Log Transcript Display Component
export const TimeLogTranscriptDisplay = ({
  timeLogs,
  currentUser,
  studentId,
  consolidateTimeLogs,
  groupTimeLogsByGrade
}) => {
  // Use studentId if provided (for teacher viewing student), otherwise use currentUser.uid (for student viewing own)
  const userId = studentId || currentUser?.uid;
  const userTimeLogs = timeLogs.filter(log => log.userId === userId && log.status === 'approved');
  const featuredTimeLogs = userTimeLogs.filter(log => log.featured);
  
  if (featuredTimeLogs.length === 0) {
    return <p className="no-posts no-posts-padding">No featured activities yet.</p>;
  }

  // Group logs by grade
  const logsByGrade = groupTimeLogsByGrade(featuredTimeLogs);
  
  return (
    <div>
      {logsByGrade.map(({ grade, logs }) => {
        const gradeLabel = grade === 'Unknown' ? 'Unknown Grade' : grade === 'Portfolio Defense' ? 'Portfolio Defense' : `${grade}th Grade`;
        const consolidatedLogs = consolidateTimeLogs(logs);
        const featuredConsolidatedLogs = consolidatedLogs.filter(consolidated => consolidated.featured);
        
        if (featuredConsolidatedLogs.length === 0) {
          return null; // Don't show grade section if no featured activities
        }
        
        return (
          <div key={grade} className="transcript-grade-section">
            <h4 className="transcript-grade-header">
              {gradeLabel}
            </h4>
            
            <div className="transcript-time-logs-list">
              {featuredConsolidatedLogs.map(consolidated => {
                return (
                  <div key={`${consolidated.activity.toLowerCase()}-${consolidated.domain}`} className="transcript-time-log-entry">
                    <div className="transcript-time-log-main">
                      <h5 className="transcript-time-log-activity">{consolidated.activity}</h5>
                      <span className="transcript-time-log-domain">{consolidated.domain.replace(/-/g, ' ').replace(/&/g, '&').replace(/\b\w/g, l => l.toUpperCase())}</span>
                    </div>
                    <div className="transcript-time-log-summary">
                      <span className="transcript-time-log-hours">{consolidated.totalHours} hours</span>
                    </div>
                    <div className="transcript-time-log-dates">
                      {new Date(consolidated.firstDate).toLocaleDateString()} - {new Date(consolidated.lastDate).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Time Logging Hook
export const useTimeLogging = ({ currentUser, userProfile }) => {
  const [timeLogs, setTimeLogs] = useState([]);
  const [newTimeLog, setNewTimeLog] = useState({ 
    activity: '', 
    domain: '', 
    hours: '', 
    startDate: '', 
    endDate: '', 
    description: '' 
  });
  const [expandedTimeLogs, setExpandedTimeLogs] = useState(new Set());
  const [editingTimeLogId, setEditingTimeLogId] = useState(null);
  const [editTimeLogForm, setEditTimeLogForm] = useState({ activity: '', domain: '', hours: '', startDate: '', endDate: '', description: '' });

  // Listen for Time Logs
  useEffect(() => {
    if (!currentUser) {
      setTimeLogs([]);
      return;
    }

    const qTimeLogs = query(collection(db, 'timeLogs'), orderBy('startDate', 'desc'));
    const unsubscribeTimeLogs = onSnapshot(qTimeLogs, (snapshot) => {
      const timeLogsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTimeLogs(timeLogsData);
    }, (error) => console.error("Error fetching time logs:", error));

    return () => unsubscribeTimeLogs();
  }, [currentUser]);

  const handleAddTimeLog = async () => {
    if (!newTimeLog.activity.trim() || !newTimeLog.domain || !newTimeLog.hours || !newTimeLog.startDate || !newTimeLog.endDate || !currentUser) {
      alert('Please fill in all required fields.');
      return;
    }

    try {
      await addDoc(collection(db, 'timeLogs'), {
        userId: currentUser.uid,
        activity: newTimeLog.activity.trim(),
        domain: newTimeLog.domain,
        hours: parseFloat(newTimeLog.hours),
        startDate: newTimeLog.startDate,
        endDate: newTimeLog.endDate,
        description: newTimeLog.description.trim(),
        grade: userProfile.grade, // Include student's grade from profile
        timestamp: new Date().toISOString(),
        featured: false,
        status: 'pending'
      });

      // Clear the form
      setNewTimeLog({ 
        activity: '', 
        domain: '', 
        hours: '', 
        startDate: '', 
        endDate: '', 
        description: '' 
      });
    } catch (error) {
      console.error("Error adding time log:", error.message);
      alert('Failed to add time log entry. Please try again.');
    }
  };

  const handleApproveTimeLog = async (entryId) => {
    try {
      const ref = doc(db, 'timeLogs', entryId);
      await updateDoc(ref, { status: 'approved' });
      setTimeLogs(prev => prev.map(t => t.id === entryId ? { ...t, status: 'approved' } : t));
    } catch (error) {
      console.error('Error approving time log entry:', error);
      alert('Failed to approve entry.');
    }
  };

  const handleStartEditTimeLog = (entry) => {
    setEditingTimeLogId(entry.id);
    setEditTimeLogForm({
      activity: entry.activity || '',
      domain: entry.domain || '',
      hours: String(entry.hours ?? ''),
      startDate: entry.startDate || '',
      endDate: entry.endDate || '',
      description: entry.description || ''
    });
  };

  const handleSaveTimeLogEdit = async () => {
    if (!editingTimeLogId) return;
    try {
      const ref = doc(db, 'timeLogs', editingTimeLogId);
      const updates = {
        activity: editTimeLogForm.activity.trim(),
        domain: editTimeLogForm.domain,
        hours: parseFloat(editTimeLogForm.hours),
        startDate: editTimeLogForm.startDate,
        endDate: editTimeLogForm.endDate,
        description: editTimeLogForm.description.trim()
      };
      await updateDoc(ref, updates);
      setTimeLogs(prev => prev.map(t => t.id === editingTimeLogId ? { ...t, ...updates } : t));
      setEditingTimeLogId(null);
      setEditTimeLogForm({ activity: '', domain: '', hours: '', startDate: '', endDate: '', description: '' });
    } catch (error) {
      console.error('Error saving time log edit:', error);
      alert('Failed to save changes.');
    }
  };

  const handleCancelEditTimeLog = () => {
    setEditingTimeLogId(null);
    setEditTimeLogForm({ activity: '', domain: '', hours: '', startDate: '', endDate: '', description: '' });
  };

  const handleToggleConsolidatedFeatured = async (consolidated) => {
    try {
      const batch = writeBatch(db);
      const newFeaturedStatus = !consolidated.featured;
      
      // Update all entries in this consolidated activity
      consolidated.entries.forEach(entry => {
        const timeLogRef = doc(db, 'timeLogs', entry.id);
        batch.update(timeLogRef, { featured: newFeaturedStatus });
      });
      
      await batch.commit();
      
      // Update local state
      setTimeLogs(prevTimeLogs => 
        prevTimeLogs.map(timeLog => 
          consolidated.entries.some(entry => entry.id === timeLog.id)
            ? { ...timeLog, featured: newFeaturedStatus }
            : timeLog
        )
      );
    } catch (error) {
      console.error('Error toggling consolidated featured status:', error);
      alert('Failed to update featured status');
    }
  };

  const handleDeleteConsolidated = async (consolidated) => {
    try {
      const confirmDelete = window.confirm('Delete all entries for this activity? This cannot be undone.');
      if (!confirmDelete) return;
      const batch = writeBatch(db);
      consolidated.entries.forEach(entry => {
        const ref = doc(db, 'timeLogs', entry.id);
        batch.delete(ref);
      });
      await batch.commit();
      setTimeLogs(prev => prev.filter(t => !consolidated.entries.some(e => e.id === t.id)));
    } catch (error) {
      console.error('Error deleting consolidated time logs:', error);
      alert('Failed to delete entries.');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      const confirmDelete = window.confirm('Delete this entry? This cannot be undone.');
      if (!confirmDelete) return;
      await deleteDoc(doc(db, 'timeLogs', entryId));
      setTimeLogs(prev => prev.filter(t => t.id !== entryId));
    } catch (error) {
      console.error('Error deleting time log entry:', error);
      alert('Failed to delete entry.');
    }
  };

  const toggleTimeLogExpansion = (activityKey) => {
    const newExpandedTimeLogs = new Set(expandedTimeLogs);
    if (newExpandedTimeLogs.has(activityKey)) {
      newExpandedTimeLogs.delete(activityKey);
    } else {
      newExpandedTimeLogs.add(activityKey);
    }
    setExpandedTimeLogs(newExpandedTimeLogs);
  };

  return {
    timeLogs,
    setTimeLogs,
    newTimeLog,
    setNewTimeLog,
    expandedTimeLogs,
    setExpandedTimeLogs,
    handleAddTimeLog,
    handleToggleConsolidatedFeatured,
    toggleTimeLogExpansion,
    handleDeleteConsolidated,
    handleDeleteEntry,
    handleApproveTimeLog,
    editingTimeLogId,
    editTimeLogForm,
    setEditTimeLogForm,
    handleStartEditTimeLog,
    handleSaveTimeLogEdit,
    handleCancelEditTimeLog
  };
};

// Utility Functions
export const timeLoggingUtils = {
  consolidateTimeLogs: (logs) => {
    const consolidated = {};
    
    logs.forEach(log => {
      const key = `${log.activity.toLowerCase()}-${log.domain}`;
      
      if (!consolidated[key]) {
        consolidated[key] = {
          activity: log.activity,
          domain: log.domain,
          totalHours: 0,
          entryCount: 0,
          firstDate: log.startDate,
          lastDate: log.endDate,
          entries: [],
          grade: log.grade,
          featured: log.featured || false
        };
      }
      
      consolidated[key].totalHours += log.hours;
      consolidated[key].entryCount += 1;
      consolidated[key].entries.push(log);
      
      // Update featured status - consolidated is featured if ALL entries are featured
      consolidated[key].featured = consolidated[key].entries.every(entry => entry.featured);
      
      // Update date range
      if (new Date(log.startDate) < new Date(consolidated[key].firstDate)) {
        consolidated[key].firstDate = log.startDate;
      }
      if (new Date(log.endDate) > new Date(consolidated[key].lastDate)) {
        consolidated[key].lastDate = log.endDate;
      }
    });
    
    return Object.values(consolidated).sort((a, b) => b.totalHours - a.totalHours);
  },

  groupTimeLogsByGrade: (logs) => {
    const grouped = {};
    logs.forEach(log => {
      const grade = log.grade || 'Unknown';
      if (!grouped[grade]) {
        grouped[grade] = [];
      }
      grouped[grade].push(log);
    });
    
    // Sort grades with Portfolio Defense first, then ascending order (8th grade first), Unknown last
    const sortedGrades = Object.keys(grouped).sort((a, b) => {
      // Portfolio Defense goes first
      if (a === 'Portfolio Defense') return -1;
      if (b === 'Portfolio Defense') return 1;
      // Handle 'Unknown' grade by putting it last
      if (a === 'Unknown') return 1;
      if (b === 'Unknown') return -1;
      return parseInt(a) - parseInt(b);
    });
    
    return sortedGrades.map(grade => ({
      grade: grade,
      logs: grouped[grade].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)) // Sort by newest first
    }));
  }
};

const timeLoggingComponents = {
  TimeLogCreationForm,
  TimeLogDisplay,
  TimeLogTranscriptDisplay,
  useTimeLogging,
  timeLoggingUtils
};

export default timeLoggingComponents;
